import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import getAddEditDutyCodeSchema from '../utils/addDutyCodeSchema';
import {
  DutyCodeForm as DutyCodeFormType,
  defaultFormValues
} from './AddDutyCode.types';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { ControlledTimePeriods } from 'components/shared/ControlledFields';
import ControlledValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import DutyCodeForm from 'components/shared/DutyCodesGrid/DutyCodeBaseFields';
import { LoadingOverlay, showSnackbar } from 'components/shared/index';

import Actions from '../../Actions';
import { StyledDialogMedium } from 'components/shared/Dialog/Dialog.styles';

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  onSave: () => void;
  organizationalUnitId?: number;
};

const AddDutyCode = ({
  isOpen,
  handleClose,
  onSave,
  organizationalUnitId
}: Props) => {
  const { t } = useTranslation('common');
  const methods = useForm<DutyCodeFormType>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(getAddEditDutyCodeSchema())
  });

  const { control, handleSubmit, reset, watch } = methods;
  const { mutate: createDutyCode, isLoading: isCreating } =
    mutations.useCreateDutyCode();

  const QueryClient = useQueryClient();

  function handleReset() {
    reset(defaultFormValues);
  }

  function onSubmit(data: DutyCodeFormType) {
    createDutyCode(
      {
        code: data.displayDutyCode,
        colorCode: data.colorCode,
        validFrom: data.validFrom.toISOString(),
        validTo: data.validTo.toISOString(),
        organizationalUnitId: organizationalUnitId,
        periods: data.periods.map((period) => ({
          dutyHourTypeId: period.dutyHourTypeId,
          startTime: period.startTime.toISOString(),
          endTime: period.endTime.toISOString()
        }))
      },
      {
        onSuccess: () => {
          QueryClient.invalidateQueries(queryKeys.getDutyCodes.name);
          onSave();
          handleReset();
        },
        onError: (error: any) => {
          const message = t(
            error?.response?.data?.error?.message || 'somethingWentWrong'
          );
          showSnackbar({
            severity: 'error',
            message: message
          });
        }
      }
    );
  }

  function onDialogClose() {
    handleClose();
    handleReset();
  }

  return (
    <StyledDialogMedium
      open={isOpen}
      title={t('addDutyCode')}
      handleClose={onDialogClose}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DutyCodeForm />
          <ControlledValidFromValidTo
            control={control}
            validFromFieldName="validFrom"
            validToFieldName="validTo"
          />
          <ControlledTimePeriods
            control={control}
            watch={watch}
            hasDHTSelector
            hideBreakDuration
            dutyHourTypeDynFlags={
              organizationalUnitId ? ['AVAILABLE_DECENTRALIZED'] : undefined
            }
          />
          <Actions onDialogClose={onDialogClose} />
          <LoadingOverlay loading={isCreating} />
        </form>
      </FormProvider>
    </StyledDialogMedium>
  );
};

export default AddDutyCode;
